import { Geolocation } from '@capacitor/geolocation';
import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonGrid,
  IonIcon,
  IonImg,
  IonItem,
  IonLabel,
  IonList,
  IonRouterLink,
  IonRow,
  IonSearchbar,
  IonToolbar,
} from '@ionic/react';
import * as Icons from 'ionicons/icons';
import * as React from 'react';
import { useMap } from 'react-map-gl/maplibre';

import { css } from '@emotion/react';
import { fetchCurrentLocation } from '../../api/services/current-location';
import { useReverseLookupLocation } from '../../api/services/reverse-lookup-location';
import { useSearchLocation } from '../../api/services/search-location';
import CircleUrl from '../../assets/img/circle.svg';
import LocateOnIconUrl from '../../assets/img/locate_on_icon.svg';
import MapPinUrl from '../../assets/img/map_pin.svg';
import SquareUrl from '../../assets/img/square.svg';

import { WLOMException } from '../../common/exceptions';
import { LocationSearchResult, SearchResultType } from '../../common/location-search-result';
import { MapData } from '../../common/map-data';
import { useSettings } from '../../contexts/SettingsContext';
import * as commonCss from '../../styles/common';
import { OpenSiteDto, SourceType, type Point } from '../../types';

import { ReactFitty } from 'react-fitty';
import { AqiScale } from '../AqiScale';
import OpenSiteIcon from '../OpenSiteIcon';
import { SkeletonText } from '../SkeletonText';
import { Spinner } from '../Spinner';
import { Title } from '../Title';
import { ActiveLocationType } from '../../common/active-location';

interface MapCardProps {
  onError: (error: WLOMException) => void;
  onGeoLocate?: (location: Point) => void;
  onSelectSearchResult?: (result: LocationSearchResult) => void;
  mapData: MapData[];
  openSites: OpenSiteDto[];
}

const MapCard: React.FC<MapCardProps> = ({ onGeoLocate, onSelectSearchResult, onError, mapData, openSites }) => {
  const { aqMap: map } = useMap();
  const {
    aqiStandard,
    network,
    mapSettings,
    pollutantConfigs,
    activeLocation,
    viewport,
    isLocationPermissionGranted,
    selectedOpenSite,
    isSettingsLoaded,
    setLocationPermissionGranted,
    setMyLocationActive,
    clearActiveLocation,
  } = useSettings();
  const { reverseLookupLocation } = useReverseLookupLocation();

  const [searchResults, setSearchResults] = React.useState<LocationSearchResult[]>([]);
  const [isSearchLoading, setIsSearchLoading] = React.useState<boolean>(false);
  const [isLoadingLocation, setIsLoadingLocation] = React.useState<boolean>(false);
  const isSearchQueryInitialized = React.useRef<boolean>(false);
  const searchbarRef = React.useRef<HTMLIonSearchbarElement>(null);
  const { searchLocation, updateSearchCache } = useSearchLocation();

  const checkLocationPermission = async (): Promise<boolean> => {
    const permissions = await Geolocation.checkPermissions();
    if (permissions.location === 'granted') {
      setLocationPermissionGranted(true);
      return true;
    }
    setLocationPermissionGranted(false);
    return false;
  };

  const setSearchQuery = (query: string) => {
    if (searchbarRef.current) {
      searchbarRef.current.value = query;
    }
  };

  // Initialize search query if empty and there's a searched location available
  React.useEffect(() => {
    if (!isSettingsLoaded || searchbarRef.current === null) {
      return;
    }

    if (!isSearchQueryInitialized.current && activeLocation) {
      if (activeLocation.type === ActiveLocationType.ReverseGeocodedLocation) {
        reverseLookupLocation(activeLocation.point).then((result) => {
          const displayName = result?.displayName ?? '';
          setSearchQuery(displayName);
        });
      } else if (activeLocation.type === ActiveLocationType.OpenSite && selectedOpenSite) {
        setSearchQuery(selectedOpenSite.displayName);
      }
    }
    isSearchQueryInitialized.current = true;
  }, [activeLocation, reverseLookupLocation, selectedOpenSite, isSettingsLoaded]);

  const toggleGeoLocate = React.useCallback(async () => {
    setIsLoadingLocation(true);
    try {
      let currentLocation: Point | null = null;
      try {
        currentLocation = await fetchCurrentLocation(mapSettings?.bbox ?? undefined);
        if (currentLocation) {
          setMyLocationActive(currentLocation);
        } else {
          clearActiveLocation();
        }
      } catch (e) {
        if (e instanceof WLOMException) {
          onError(e);
        }
      }
      if (!(await checkLocationPermission()) || !currentLocation) {
        return;
      }
      onGeoLocate?.(currentLocation);
    } finally {
      setIsLoadingLocation(false);
    }
  }, [checkLocationPermission, onGeoLocate, onError, mapSettings?.bbox, clearActiveLocation, setMyLocationActive]);

  const onClickGeoLocate = React.useCallback(async () => {
    if (activeLocation?.type === ActiveLocationType.MyLocation) {
      clearActiveLocation();
    } else {
      toggleGeoLocate();
    }
  }, [clearActiveLocation, activeLocation?.type, toggleGeoLocate]);

  const onClickSearchResult = React.useCallback(
    (result: LocationSearchResult) => {
      setSearchResults([]);
      setSearchQuery(result.label);
      onSelectSearchResult?.(result);
    },
    [onSelectSearchResult]
  );

  const handleSearchInput = React.useCallback(
    async (e: Event) => {
      const target = e.target as HTMLIonSearchbarElement;
      if (!target) {
        return;
      }
      setIsSearchLoading(true);
      try {
        const query = target.value!;
        if (query.length > 2) {
          const suggestions = await searchLocation(query, viewport, mapData, openSites, mapSettings?.bbox ?? undefined);
          setSearchResults(suggestions);
        } else {
          setSearchResults([]);
        }
      } finally {
        setIsSearchLoading(false);
      }
    },
    [mapSettings?.bbox, mapData, searchLocation, viewport, openSites]
  );

  const handleClearSearch = React.useCallback(() => {
    setSearchResults([]);
    setSearchQuery('');
    clearActiveLocation();
  }, [clearActiveLocation]);

  React.useEffect(() => {
    updateSearchCache(mapData, openSites);
  }, [mapData, openSites, updateSearchCache]);

  const mapCardCss = {
    card: [
      css({
        zIndex: 4,
        width: 400,
        margin: 0,
        [commonCss.MQSmallerThan.sm]: {
          width: '100%',
        },
      }),
    ],
    cardHeader: [commonCss.noPadding],
    cardContent: [
      css({
        padding: 8,
      }),
    ],
    locationSuggestionList: [
      css({
        maxHeight: '50vh',
        overflowY: 'auto',
      }),
    ],

    pollutantSelector: [css({ width: '5rem' })],
    geolocateButton: [
      css({
        width: '2.55rem',
        height: '2.55rem',
        '.md &': {
          boxShadow:
            'rgba(0, 0, 0, 0.2) 0px 2px 2px 0px, rgba(0, 0, 0, 0.14) 0px 3px 1px -2px, rgba(0, 0, 0, 0.12) 0px 1px 5px 0px',
        },
        '.ios &': {
          width: 'calc(1.0625rem + 12px)',
          height: 'calc(1.5625rem + 12px)',
        },
      }),
    ],
    toolbar: [
      css({
        borderBottom: '1px solid',
        borderColor: 'var(--border-color)',
        '--background': 'transparent',
      }),
    ],
    searchbar: [
      css({
        position: 'relative',
        '--background': '#fff',
        ['.searchbar-input-container.sc-ion-searchbar-ios']: {
          border: '1px solid',
          borderColor: 'var(--ion-background-color-step-150)',
          borderRadius: 4,
        },
        [commonCss.MQSmallerThan.sm]: {
          ['.ios &']: {
            padding: '0 8px 0 0',
          },
        },
      }),
    ],
    clearButton: css({
      fontSize: '1.5rem',
      position: 'absolute',
      right: '4rem',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1,
      cursor: 'pointer',
    }),
    searchResultOutOfBounds: [
      css({
        color: 'var(--ion-color-grey)',
        cursor: 'not-allowed',
      }),
    ],
    searchResultText: [
      css({
        marginLeft: '1rem',
      }),
    ],
    topRow: [css({ display: 'flex', padding: '0.5rem' })],
    logoImg: [css({ display: 'flex', flex: 1, height: 70, marginRight: '1rem' })],
    title: [css({ display: 'flex', flex: 2, alignItems: 'center' })],
  };

  const networkHeader = network ? (
    <div css={mapCardCss.topRow}>
      <IonImg src={network.logoUrl} alt={network.name} css={mapCardCss.logoImg} />
      <div css={mapCardCss.title}>
        <ReactFitty maxSize={22}>
          <Title fontSize="inherit">{network.name}</Title>
        </ReactFitty>
      </div>
    </div>
  ) : null;

  return (
    <>
      <IonCard css={mapCardCss.card} id="map-card-content">
        <IonCardHeader css={mapCardCss.cardHeader}>
          <IonToolbar css={mapCardCss.toolbar}>
            {network ? (
              network.networkUrl ? (
                <IonRouterLink href={network.networkUrl} target="_blank" rel="noopener noreferrer">
                  {networkHeader}
                </IonRouterLink>
              ) : (
                networkHeader
              )
            ) : (
              <SkeletonText height={86} />
            )}
          </IonToolbar>
        </IonCardHeader>
        <IonCardContent css={mapCardCss.cardContent}>
          <IonGrid>
            <IonRow>
              <IonCol>
                <AqiScale popOverAlignment="end" popOverSide="right" />
              </IonCol>
            </IonRow>
            {map ? (
              <IonButtons>
                <IonSearchbar
                  ref={searchbarRef}
                  placeholder="Search place"
                  onIonInput={handleSearchInput}
                  debounce={500}
                  showClearButton="never"
                  css={mapCardCss.searchbar}
                  className="tour-searchbar"
                  searchIcon={isSearchLoading ? Icons.ellipsisHorizontal : Icons.search}
                />
                {((searchbarRef.current?.value?.length ?? 0) > 0 ||
                  searchResults.length > 0 ||
                  [
                    ActiveLocationType.ReverseGeocodedLocation,
                    ActiveLocationType.Datasource,
                    ActiveLocationType.OpenSite,
                  ].includes(activeLocation?.type ?? ('' as ActiveLocationType))) && (
                  <IonIcon icon={Icons.close} css={mapCardCss.clearButton} onClick={handleClearSearch} />
                )}
                <IonButton
                  disabled={isLocationPermissionGranted === false}
                  onClick={onClickGeoLocate}
                  css={mapCardCss.geolocateButton}
                  fill="solid"
                  color="primary"
                  className="tour-geolocate-button"
                >
                  {!isLoadingLocation ? (
                    activeLocation?.type === ActiveLocationType.MyLocation ? (
                      <IonIcon src={LocateOnIconUrl} slot="icon-only" />
                    ) : (
                      <IonIcon icon={Icons.locate} slot="icon-only" />
                    )
                  ) : (
                    <Spinner type="dots" slot="icon-only" />
                  )}
                </IonButton>
              </IonButtons>
            ) : (
              <IonRow>
                <IonCol>
                  <SkeletonText height={40} />
                </IonCol>
                <IonCol size="2">
                  <SkeletonText height={40} />
                </IonCol>
              </IonRow>
            )}
            {aqiStandard && pollutantConfigs && searchResults.length > 0 && (
              <IonRow>
                <IonCol size="12">
                  <IonList css={mapCardCss.locationSuggestionList}>
                    {searchResults.map((result, index) =>
                      result.isOutOfBounds ? (
                        <IonItem
                          css={[commonCss.smallText, mapCardCss.searchResultOutOfBounds]}
                          key={index}
                          button={false}
                        >
                          <IonIcon src={MapPinUrl} css={mapCardCss.searchResultText}></IonIcon>
                          <IonLabel>(Outside of network) {result.label}</IonLabel>
                        </IonItem>
                      ) : (
                        <IonItem
                          css={commonCss.smallText}
                          key={index}
                          button
                          onClick={() => onClickSearchResult(result)}
                        >
                          {result.type === SearchResultType.Datasource ? (
                            <IonIcon
                              icon={result.mapData!.sourceType === SourceType.CLARITY_NODE ? CircleUrl : SquareUrl}
                              css={css({ color: result.mapData!.getColor(aqiStandard, pollutantConfigs) })}
                            />
                          ) : result.type === SearchResultType.OpenSite ? (
                            network?.defaultOpenSiteIcon ? (
                              <OpenSiteIcon icon={network.defaultOpenSiteIcon}></OpenSiteIcon>
                            ) : null
                          ) : (
                            <IonIcon src={MapPinUrl}></IonIcon>
                          )}
                          <IonLabel
                            css={[
                              mapCardCss.searchResultText,
                              result.type !== SearchResultType.Place ? commonCss.bold : {},
                            ]}
                          >
                            {result.label}
                          </IonLabel>
                        </IonItem>
                      )
                    )}
                  </IonList>
                </IonCol>
              </IonRow>
            )}
          </IonGrid>
        </IonCardContent>
      </IonCard>
    </>
  );
};

export default MapCard;
